import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { IFieldProps } from "types/Organisational";

import { useOrganisation } from "@RequestFormV2/sections/Organisation/context";
import { mapDropdownOptions } from "@RequestFormV2/sections/Organisation/hooks/organisationFieldOptions";

const isCostRecoverySubField = (fieldId: string) => fieldId.startsWith("custom_cost_recovery_");

export function useCostRecoveryFields() {
  const { organisationFields, yesValue } = useOrganisation();
  const { setValue, getValues } = useFormContext();

  const [reasonCode, costRecoveryRequired] = useWatch({
    name: ["organizational.reason_code", "organizational.cost_recovery_required"],
  });

  const costRecoveryField = useMemo(
    () => organisationFields?.find((f) => f.field_id === "cost_recovery_required"),
    [organisationFields],
  );

  const costRecoverySubFields = useMemo(
    () => organisationFields?.filter((f) => isCostRecoverySubField(f.field_id)) ?? [],
    [organisationFields],
  );

  const showCostRecovery = useMemo(() => {
    if (!organisationFields?.length) return false;
    if (costRecoveryField?.mandatory) return true;
    const reasonCodeField = organisationFields.find((f) => f.field_id === "reason_code");
    if (reasonCode && reasonCodeField?.dropdown_values?.length) {
      const selected = (reasonCodeField.dropdown_values as any[]).find((item) => item.value === reasonCode);
      return !!selected?.cost_recovery_possible;
    }
    return false;
  }, [organisationFields, costRecoveryField, reasonCode]);

  const radioOptions = useMemo(() => {
    const options =
      (costRecoveryField?.dropdown_values as any[])?.map((item) => ({
        label: item.key,
        value: item.value,
      })) ?? [];

    return [...options].sort((a, b) => {
      if (a.value === yesValue) return -1;
      if (b.value === yesValue) return 1;
      return 0;
    });
  }, [costRecoveryField, yesValue]);

  const getSubFieldDropdownOptions = (field: IFieldProps) => mapDropdownOptions(field);

  useEffect(() => {
    const isCostRecoveryNo = costRecoveryRequired === "False" || costRecoveryRequired === false;
    if (!isCostRecoveryNo) return;
    // Backend treats null as missing for mandatory custom cost-recovery fields,
    // but accepts "". Converted QR drafts often ship null — always normalize to "".
    costRecoverySubFields.forEach(({ field_id }) => {
      const fieldName = `organizational.${field_id}`;
      if (getValues(fieldName) !== "") setValue(fieldName, "");
    });
  }, [costRecoveryRequired, costRecoverySubFields, getValues, setValue]);

  return {
    costRecoveryField,
    yesValue,
    showCostRecovery,
    costRecoverySubFields,
    radioOptions,
    getSubFieldDropdownOptions,
    showSubFields: costRecoveryRequired === yesValue,
  };
}
